import { inject, Injectable } from '@angular/core';
import { generateGradient, generateShades } from '@core/functions/colors.function';
import { ClientConfig, Shades } from '@core/models/client.model';
import { ClientStore } from '@core/state/client/client.store';
import { EnvironmentService } from '../environment/environment.service';
import { d2dTheme } from '@core/theme/d2d-theme';
import { updatePreset } from '@primeuix/themes';

/**
 * Orquestación DOM-theme + atomic store mutations. State lives in ClientStore;
 * components read it via `clientStore.clientConfig` (signal). This service is
 * the only place that should call `setConfig` / `unSetConfig` (resolver +
 * default page-layout) so theme application stays atomic with state changes.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientConfigService {
  private readonly environmentService = inject(EnvironmentService);
  private readonly clientStore = inject(ClientStore);

  primaryColor = d2dTheme.primitive.gold[500];
  secondaryColor = d2dTheme.primitive.oil[500];

  /** Atomic: store mutation + theme application. */
  setConfig(config: ClientConfig): void {
    this.clientStore.setConfig(config);
    this.changeTheme(config);
  }

  /** Atomic: store reset + theme reset to defaults. */
  unSetConfig(): void {
    this.clientStore.unsetConfig();
    this.changeTheme();
  }

  changeTheme(config?: ClientConfig): void {
    if (this.environmentService.isServer()) return;

    const clientPreset = { ...d2dTheme };

    const primaryShades = this.generateShades(config?.primary_color || this.primaryColor);
    this.updateShades(primaryShades, '--gold-color');

    const secondaryShades = this.generateShades(config?.secondary_color || this.secondaryColor);
    this.updateShades(secondaryShades, '--oil-color');

    clientPreset.primitive.gold = primaryShades;
    clientPreset.primitive.oil = secondaryShades;
    clientPreset.semantic.primary = primaryShades;

    updatePreset(clientPreset);

    document.documentElement.style.setProperty(
      '--header-bg-color',
      config?.header_bg_color || '#ffffff',
    );
    document.documentElement.style.setProperty(
      '--header-text-color',
      config?.header_text_color || '#00192C',
    );
    document.documentElement.style.setProperty(
      '--header-button-bg-color',
      config?.header_button_bg_color || '#B1802E',
    );
    document.documentElement.style.setProperty(
      '--header-button-text-color',
      config?.header_button_text_color || '#ffffff',
    );
    document.documentElement.style.setProperty(
      '--bg-home-degree',
      generateGradient(config?.bg_color || '#00192C'),
    );
    document.documentElement.style.setProperty(
      '--bg-home-image',
      `url('${config?.banner_url ?? './images/home-bg-3.webp'}')`,
    );
    document.documentElement.style.setProperty(
      '--main-title-color',
      config?.main_title_color || '#ffffff',
    );
    document.documentElement.style.setProperty(
      '--main-subtitle-color',
      config?.main_subtitle_color || '#ffffff',
    );
    document.documentElement.style.setProperty(
      '--cajon-bg-color',
      config?.cajon_bg_color
        ? this.toRgba(config.cajon_bg_color, 0.85)
        : 'rgba(255, 255, 255, 0.15)',
    );
    document.documentElement.style.setProperty(
      '--box-text-color',
      config?.box_text_color || '#ffffff',
    );

    this.updateFavicon(config?.logo_url);
  }

  /**
   * Updates the browser favicon and apple-touch-icon to match the client's logo.
   * Restores D2D defaults when no logoUrl is provided (i.e. when unSetConfig is called).
   */
  private updateFavicon(logoUrl?: string): void {
    const DEFAULT_FAVICON = './icons/logo-d2d.svg';
    const DEFAULT_TOUCH_ICON = 'icons/logo_2d2_small.png';

    const faviconUrl = logoUrl || DEFAULT_FAVICON;
    const touchIconUrl = logoUrl || DEFAULT_TOUCH_ICON;

    // Update <link rel="icon">
    let favicon: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link') as HTMLLinkElement;
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('type', logoUrl ? 'image/png' : 'image/x-icon');
    favicon.setAttribute('href', faviconUrl);

    // Update <link rel="apple-touch-icon">
    const touchSelectors = [
      "link[rel='apple-touch-icon']",
      "link[rel='apple-touch-icon-precomposed']",
    ];
    touchSelectors.forEach((selector) => {
      const el: HTMLLinkElement | null = document.querySelector(selector);
      if (el) {
        el.setAttribute('href', touchIconUrl);
      }
    });
  }

  private toRgba(color: string, alpha: number): string {
    if (color.startsWith('rgba(') || color.startsWith('rgb(')) {
      return color;
    }
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private updateShades(shades: Record<number, string> | Shades, variable = '--p-primary'): void {
    Object.entries(shades).forEach(([shade, value]) => {
      document.documentElement.style.setProperty(`${variable}-${shade}`, value);
    });
  }

  private generateShades(color: string): Shades {
    return this.mapShadesToObject(generateShades(color));
  }

  private mapShadesToObject(shadesArray: string[]): Shades {
    const shadeKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

    return shadeKeys.reduce((acc, key, index) => {
      acc[key] = shadesArray[index];
      return acc;
    }, {} as Shades);
  }
}
